import {
  enumCatalogShareStatus,
  type CatalogShareResourceReadBffApi,
  type EnumCatalogShareStatusApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { toLower } from 'lodash-es';
import type { CatalogShareDetails } from '../../models/catalogShare.model';
import { CatalogShareDetailsStatus } from '../../models/catalogShare.model';
import { toIsoStringIfDate } from '../utils/date.utils';

const CATALOG_SHARE_STATUS_TYPE: Record<CatalogShareDetailsStatus, CatalogShareDetails['status']['type']> = {
  [CatalogShareDetailsStatus.PROCESSING]: 'info',
  [CatalogShareDetailsStatus.ACTIVE]: 'success',
  [CatalogShareDetailsStatus.FAILED]: 'error',
  [CatalogShareDetailsStatus.EXPIRED]: 'neutral',
  [CatalogShareDetailsStatus.REVOKED]: 'neutral',
};

const MAPPER_CATALOG_SHARE_STATUS_BACKEND_TO_LOCAL: Record<EnumCatalogShareStatusApi, CatalogShareDetailsStatus> = {
  [enumCatalogShareStatus.PENDING]: CatalogShareDetailsStatus.PROCESSING,
  [enumCatalogShareStatus.ACTIVE]: CatalogShareDetailsStatus.ACTIVE,
  [enumCatalogShareStatus.EXPIRED]: CatalogShareDetailsStatus.EXPIRED,
  [enumCatalogShareStatus.FAILED]: CatalogShareDetailsStatus.FAILED,
  [enumCatalogShareStatus.REVOKED]: CatalogShareDetailsStatus.REVOKED,
};

export function getCatalogShareDetailsByStatus({
  statusMetadata,
}: CatalogShareResourceReadBffApi): Pick<
  CatalogShareDetails,
  'activationDate' | 'expirationDate' | 'revocationDate' | 'revokedBy' | 'failureReason'
> {
  const { active, expired, revoked } = statusMetadata;

  return {
    activationDate: active?.activatedAt?.toISOString(),
    expirationDate: expired?.expiredAt?.toISOString(),
    revocationDate: revoked?.revokedAt?.toISOString(),
    revokedBy: revoked?.revokedBy?.name,
    failureReason: undefined,
    /**
     * Disclosured defined by Hichem on the ticket https://ingenico-tech.atlassian.net/browse/MDMGT-8587:
        failureReason won't be processed for now.
        Intended to use the variable details?.failureReason to show the expected message (perhaps with an internal mapper to translation keys)
        Ticket expected to use this value on the commented Banner component on the view (CatalogShareDetailsDrawer.ctrl)
        expected ticket to implement the failure reason: https://ingenico-tech.atlassian.net/browse/MDMGT-8586
     */
  };
}

export function mapCatalogShareDetailsStatus({ status }: CatalogShareResourceReadBffApi): CatalogShareDetailsStatus {
  return MAPPER_CATALOG_SHARE_STATUS_BACKEND_TO_LOCAL[status] ?? CatalogShareDetailsStatus.FAILED;
}

export function mapCatalogShareDetails(catalogShareDetails: CatalogShareResourceReadBffApi): CatalogShareDetails {
  const statusCode = mapCatalogShareDetailsStatus(catalogShareDetails);

  return {
    ...getCatalogShareDetailsByStatus(catalogShareDetails),
    id: catalogShareDetails.id,
    versionNumber: catalogShareDetails.target.versionMetadata.versionNumber,
    status: {
      code: statusCode,
      label: toLower(statusCode),
      type: CATALOG_SHARE_STATUS_TYPE[statusCode],
    },
    creationDate: toIsoStringIfDate(catalogShareDetails.createdAt),
    createdBy: catalogShareDetails.createdBy.name,
    recipients: {
      count: catalogShareDetails.recipients.length,
      emails: catalogShareDetails.recipients.map(({ normalizedEmail }) => normalizedEmail),
    },
  };
}
